/**
 * domain.ts — rene hjelpefunksjoner for domenesøk (ingen nettverkskall)
 *
 * parseDomainQuery tolker det brukeren skriver (med eller uten endelse, med
 * eventuell protokoll/«www.»/sti) og validerer navnet mot DNS-reglene:
 * 1–63 tegn per ledd, bare bokstaver/tall/bindestrek, ikke bindestrek først
 * eller sist. Norske tegn (æ, ø, å) godtas og konverteres til punycode via
 * URL-APIet for lengdesjekken. Funksjonen sjekker IKKE tilgjengelighet — det må
 * en backend gjøre (se plan: POST /api/public/domain/check).
 */

export type DomainParseResult =
  | { ok: true; name: string; tld: string | null }
  | { ok: false; error: 'empty' | 'invalid' | 'tooLong' | 'unsupportedTld'; tld?: string };

/** Fjerner protokoll, «www.», sti og spørring, og gjør om til små bokstaver */
export function normalizeQuery(raw: string): string {
  let q = raw.trim().toLowerCase().normalize('NFC');
  q = q.replace(/^[a-z][a-z0-9+.-]*:\/\//, ''); // protokoll
  q = q.split(/[/?#]/)[0]; // sti, spørring, anker
  q = q.replace(/^www\./, '');
  return q;
}

function asciiLabel(label: string): string | null {
  try {
    return new URL(`http://${label}.example`).hostname.replace(/\.example$/, '');
  } catch {
    return null;
  }
}

/** @param supportedTlds endelser uten punktum, f.eks. ['no', 'com'] */
export function parseDomainQuery(raw: string, supportedTlds: string[]): DomainParseResult {
  const q = normalizeQuery(raw);
  if (!q) return { ok: false, error: 'empty' };

  const parts = q.split('.');
  let name = parts[0];
  let tld: string | null = null;

  if (parts.length > 1) {
    tld = parts[parts.length - 1];
    name = parts.slice(0, -1).join('.');
    // «dittnavn.co.uk»-lignende navn: kun enkelt ledd støttes
    if (name.includes('.')) return { ok: false, error: 'invalid' };
    if (!supportedTlds.includes(tld)) return { ok: false, error: 'unsupportedTld', tld };
  }

  if (!name || !/^[\p{L}\p{N}-]+$/u.test(name) || name.startsWith('-') || name.endsWith('-')) {
    return { ok: false, error: 'invalid' };
  }
  const ascii = asciiLabel(name);
  if (!ascii) return { ok: false, error: 'invalid' };
  if (ascii.length > 63) return { ok: false, error: 'tooLong' };

  return { ok: true, name, tld };
}

/** Pris som tekst, f.eks. formatPrice(99, 'kr') → «99 kr» (tusenskille etter norsk skrivemåte) */
export function formatPrice(amount: number, currency: string): string {
  return `${amount.toLocaleString('nb-NO')} ${currency}`;
}
